/**
 * TriggerBreakdownChart — horizontal bar showing trigger frequency
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const COLORS = ['#ef4444', '#f59e0b', '#f97316', '#eab308', '#84cc16', '#10b981'];

const TriggerBreakdownChart = ({ data, indexCode = 'VN30' }) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{t('highVolRegime.noData', 'No data')}</Typography>
      </Box>
    );
  }

  const counts = { volexp: 0, wrdd: 0, distribution: 0, trendFragile: 0, breadthWeak: 0, basisStress: 0 };
  const labels = {
    volexp: t('highVolRegime.triggerTypes.volexp', 'Volatility Expansion'),
    wrdd: t('highVolRegime.triggerTypes.wrdd', 'Wide Range Down'),
    distribution: t('highVolRegime.triggerTypes.distribution', 'Distribution'),
    trendFragile: t('highVolRegime.triggerTypes.trendFragile', 'Trend Fragile'),
    breadthWeak: t('highVolRegime.triggerTypes.breadthWeak', 'Breadth Weak'),
    basisStress: t('highVolRegime.triggerTypes.basisStress', 'Basis Stress'),
  };

  data.forEach(item => {
    if (item.volexp_trigger) counts.volexp++;
    if (item.wrdd_count_5d > 0) counts.wrdd++;
    if (item.dist_count_20d >= 3) counts.distribution++;
    if (item.trend_fragile) counts.trendFragile++;
    if (item.breadth?.breadth_weak) counts.breadthWeak++;
    if (item.derivatives?.basis_stress) counts.basisStress++;
  });

  const chartData = Object.entries(counts)
    .map(([key, value]) => ({ name: labels[key], count: value, pct: ((value / data.length) * 100).toFixed(1) }))
    .sort((a, b) => b.count - a.count);

  const maxCount = Math.max(...chartData.map(d => d.count));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', mb: 0.2 }}>{d.name}</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>{t('highVolRegime.triggeredTimes', { count: d.count, pct: d.pct, defaultValue: `Triggered ${d.count} times (${d.pct}%)` })}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
            {indexCode} — {t('highVolRegime.charts.triggerBreakdown', 'Trigger Breakdown')}
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', px: 0.6, py: 0.15, bgcolor: '#f8fafc', borderRadius: 1, fontFamily: 'monospace' }}>
            {data.length}d
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
          {t('highVolRegime.charts.triggerDesc', 'Frequency of each signal')}
        </Typography>
      </Box>

      {/* Chart */}
      <Box sx={{ px: 1, py: 1, flex: 1 }}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis type="number" domain={[0, maxCount + 2]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} width={130} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Most active */}
      {chartData.length > 0 && (
        <Box sx={{ px: 2, pb: 1.2, pt: 0.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{t('highVolRegime.mostActive', 'Most Active')}</Typography>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b' }}>
            {chartData[0].name} ({chartData[0].count}x, {chartData[0].pct}%)
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TriggerBreakdownChart;
